import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { http } from '@noor/api';
import * as z from 'zod';

const offerSchema = z.object({
  order_id: z.union([z.string(), z.number()]),
  time: z.number(),
  to_courier: z.object({
    id: z.union([z.string(), z.number()]),
    location: z.object({ lat: z.number().finite(), long: z.number().finite() }),
  }),
});

// One server subscription per order, shared across map/fullscreen remounts.
// Queue writes so effect replay cannot unsubscribe a newer active subscriber.
const subscriptions = new Map<
  string,
  { users: number; active: boolean; queue: Promise<void> }
>();
function subscribe(orderId: string | number) {
  const key = String(orderId);
  const entry = subscriptions.get(key) ?? {
    users: 0,
    active: false,
    queue: Promise.resolve(),
  };
  subscriptions.set(key, entry);
  entry.users++;
  const synchronize = () => {
    entry.queue = entry.queue
      .then(async () => {
        if (entry.users > 0 && !entry.active) {
          await http.post(`orders/${orderId}/offer`);
          entry.active = true;
        } else if (entry.users === 0 && entry.active) {
          await http.delete(`orders/${orderId}/offer`);
          entry.active = false;
        }
      })
      .catch(() => undefined);
  };
  synchronize();
  return () => {
    entry.users--;
    synchronize();
  };
}

/** Search offers expire after 25 seconds, exactly as the legacy order map. */
export function useOrderOffer(orderId: string | number, stage: number) {
  const { data } = useQuery<unknown>({
    queryKey: ['order-courier-offer'],
    queryFn: () => null,
    enabled: false,
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (stage !== 6) return;
    const unsubscribe = subscribe(orderId);
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(timer);
      unsubscribe();
    };
  }, [orderId, stage]);
  const offer = offerSchema.safeParse(data);
  return stage === 6 &&
    offer.success &&
    String(offer.data.order_id) === String(orderId) &&
    now - offer.data.time <= 25_000
    ? offer.data.to_courier
    : null;
}
